import duoModelUrl from "@/assets/duo-star-white.glb?url";
import { RemoteControl, type PlaybackStats } from "@limrun/ui";
import { Cable, Inspect, Loader } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode, type Ref } from "react";
import { ConnectDialog } from "@/components/android-instances/ConnectDialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { useCameraStatus } from "@/components/useCameraStatus";
import { DeviceInspector } from "./DeviceInspector";
import { DeviceWorkspace } from "./DeviceWorkspace";
import { PlaybackStatsPanel } from "./PlaybackStatsPanel";
import { useMicrophoneErrorToast } from "./LiveStreamBox";
import { LiveCapturePanels, LiveNetworkPanel, tunnelApiUrlFor } from "./SessionCapturePanels";

export type DeviceConnection = {
  instanceId?: string;
  platform?: "ios" | "android";
  url: string;
  token: string;
  apiUrl?: string;
  adbWebSocketUrl?: string;
  mcpUrl?: string;
};

// Routes supply their credentials and permitted actions without bringing organization lookups into shared links.
export function DeviceSession({
  title, status, connection, ready, canUseTools, showFrame, isDuo = false,
  actions, message, error, errorAction, fallback, streamContainerRef,
}: {
  title: string;
  status: ReactNode;
  connection?: DeviceConnection;
  ready: boolean;
  canUseTools: boolean;
  showFrame: boolean;
  isDuo?: boolean;
  actions?: ReactNode;
  message?: string | null;
  error?: string;
  errorAction?: ReactNode;
  fallback?: ReactNode;
  streamContainerRef?: Ref<HTMLDivElement>;
}) {
  const [inspectMode, setInspectMode] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [inspectorVisible, setInspectorVisible] = useState(false);
  const [statsSelected, setStatsSelected] = useState(false);
  const statsActive = inspectorVisible && statsSelected;
  const [playbackStats, setPlaybackStats] = useState<PlaybackStats | null>(null);
  // Each viewer needs its own peer session, even when several links target the same device.
  const sessionId = useMemo(() => `${connection?.instanceId ?? "stream"}-${crypto.randomUUID()}`, [connection?.instanceId]);
  const { cameraProps, pill, active, granted } = useCameraStatus();
  const onMicrophoneStateChange = useMicrophoneErrorToast();
  const toolsDisabled = !ready || !!message || !!error;
  const canConnect = canUseTools && !!connection?.instanceId;
  const statsPanel = connection && <PlaybackStatsPanel
    key={`${sessionId}:${connection.url}`} stats={statsActive ? playbackStats : null} />;
  useEffect(() => { if (!statsActive) setPlaybackStats(null); }, [statsActive]);

  useEffect(() => {
    if (!inspectMode) return;
    const exitInspect = (event: KeyboardEvent) => {
      if (event.key === "Escape") setInspectMode(false);
    };
    window.addEventListener("keydown", exitInspect);
    return () => window.removeEventListener("keydown", exitInspect);
  }, [inspectMode]);

  useEffect(() => {
    if (!active || granted !== false) return;
    toast({
      title: "Camera unavailable",
      description: "The device asked for camera access but the browser denied it. The app will see black frames until permission is granted (try the address-bar camera icon).",
      variant: "destructive",
    });
  }, [active, granted]);

  return (
    <>
      <DeviceWorkspace
        title={title}
        status={status}
        message={message}
        error={error}
        errorAction={errorAction}
        actions={<>
          {pill}
          {canUseTools && <Button size="sm" variant={inspectMode ? "secondary" : "ghost"}
            disabled={isDuo || toolsDisabled} aria-pressed={inspectMode}
            onClick={() => setInspectMode(value => !value)}
            title={isDuo ? "Inspection is unavailable on iPhone Duo" : "Inspect accessibility elements"}>
            <Inspect />Inspect
          </Button>}
          {canConnect && <Button size="sm" variant="ghost" disabled={toolsDisabled} onClick={() => setConnectOpen(true)}>
            <Cable />Connect
          </Button>}
          {actions}
        </>}
        device={fallback ?? (connection ? (
          <div ref={streamContainerRef} className="flex h-full min-h-0 w-full items-center justify-center">
            <RemoteControl
              duoModelUrl={duoModelUrl}
              deviceModel={isDuo ? "iphone-duo" : undefined}
              url={connection.url}
              token={connection.token}
              showFrame={showFrame}
              autoReconnect
              inspectMode={canUseTools && !isDuo && inspectMode}
              sessionId={sessionId}
              {...cameraProps}
              onPlaybackStats={statsActive ? setPlaybackStats : undefined}
              onMicrophoneStateChange={onMicrophoneStateChange}
            />
          </div>
        ) : (
          <div role="status" className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
            <Loader className="size-5 animate-spin" /><p>Preparing your device...</p>
          </div>
        ))}
        onInspectorVisibilityChange={setInspectorVisible}
        inspector={canUseTools && connection?.apiUrl ? (
          <LiveCapturePanels embedded apiUrl={connection.apiUrl} token={connection.token}
            signalingUrl={connection.url} platform={connection.platform} className="h-full"
            stats={statsPanel} onStatsActiveChange={setStatsSelected}
            network={<LiveNetworkPanel embedded
              tunnelApiUrl={connection.platform ? tunnelApiUrlFor(connection.platform, connection) : undefined}
              token={connection.token} platform={connection.platform} instanceId={connection.instanceId} className="h-full" />}
          />
        ) : statsPanel && <DeviceInspector className="h-full" stats={statsPanel} onStatsActiveChange={setStatsSelected} />}
      />
      {canConnect && <ConnectDialog isOpen={connectOpen} onOpenChange={setConnectOpen}
        instanceId={connection.instanceId!} apiUrl={connection.apiUrl} adbWebSocketUrl={connection.adbWebSocketUrl}
        mcpUrl={connection.mcpUrl} token={connection.token} />}
    </>
  );
}
